import type { UpdateMultiselectIncludeExcludeProps } from '@terminal/components/filter/include-exclude/multiselect/MultiselectIncludeExcludeOptions.types';
import { getFilterOptionGroupBySelectionMode } from '@terminal/components/filter/include-exclude/utils/include-exclude.utils';
import type { FilterGroupKeys } from '@terminal/hooks/use-filter-actions/useFilterActions';

export const updateMultiselectIncludeExcludeSelection = <
  T extends FilterGroupKeys,
>({
  addFilterOption,
  checked,
  defaultOption,
  filterId,
  filterKey,
  filterOptionGroups,
  minimumSelection,
  option,
  removeFilterOption,
  selectionMode,
}: UpdateMultiselectIncludeExcludeProps<T>) => {
  const selectionGroup = getFilterOptionGroupBySelectionMode(
    filterOptionGroups,
    selectionMode
  );
  const shouldUseMinimumSelection =
    Boolean(minimumSelection) &&
    Boolean(defaultOption) &&
    filterOptionGroups.reduce(
      (count, group) => count + group.options.length,
      0
    ) === 1;

  if (checked) {
    if (shouldUseMinimumSelection && option.key === defaultOption?.key) {
      return;
    }

    removeFilterOption({
      filterKey,
      filterId: selectionGroup?.id ?? filterId,
      multiselect: true,
      option,
      selectionMode,
    });

    if (shouldUseMinimumSelection && defaultOption) {
      const includeSelectionGroup = getFilterOptionGroupBySelectionMode(
        filterOptionGroups,
        'include'
      );

      addFilterOption({
        filterKey,
        filterId: includeSelectionGroup?.id ?? filterId,
        multiselect: true,
        option: defaultOption,
        selectionMode: 'include',
      });
    }

    return;
  }

  const oppositeSelectionMode =
    selectionMode === 'include' ? 'exclude' : 'include';
  const oppositeSelectionGroup = getFilterOptionGroupBySelectionMode(
    filterOptionGroups,
    oppositeSelectionMode
  );
  const isSelectedInOppositeMode = oppositeSelectionGroup?.options.some(
    selectedOption => selectedOption.key === option.key
  );

  if (oppositeSelectionGroup && isSelectedInOppositeMode) {
    removeFilterOption({
      filterKey,
      filterId: oppositeSelectionGroup.id,
      multiselect: true,
      option,
      selectionMode: oppositeSelectionMode,
    });
  }

  addFilterOption({
    filterKey,
    filterId: selectionGroup?.id ?? filterId,
    multiselect: true,
    option,
    selectionMode,
  });
};
